import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCcw, LogOut, MailOpen, Mail } from "lucide-react";

const TOKEN_KEY = "savage_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [inputToken, setInputToken] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const authed = Boolean(token);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/enquiries", {
        headers: { "X-Admin-Token": token },
      });
      setRows(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid token");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load enquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    localStorage.setItem(TOKEN_KEY, inputToken.trim());
    setToken(inputToken.trim());
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
  };

  const toggleStatus = async (row) => {
    const newStatus = row.status === "read" ? "new" : "read";
    try {
      await api.patch(
        `/enquiries/${row.id}`,
        { status: newStatus },
        { headers: { "X-Admin-Token": token } }
      );
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r))
      );
    } catch {
      toast.error("Update failed");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="bg-[#17171B] border border-white/10 p-10 w-full max-w-md"
          data-testid="admin-login-form"
        >
          <div className="eyebrow">Savage Cleaning</div>
          <h1 className="mt-3 font-serif-display text-3xl text-white">Admin sign in</h1>
          <p className="mt-2 text-white/60 text-sm">Enter your admin token to view enquiries.</p>
          <label className="field-label mt-8">Admin token</label>
          <input
            type="password"
            className="field-input"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Paste admin token"
            data-testid="admin-token-input"
          />
          <button type="submit" className="btn-primary mt-8 w-full justify-center" data-testid="admin-login-button">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <header className="border-b border-white/10 sticky top-0 bg-[#0A0A0B]/90 backdrop-blur-md z-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="eyebrow">Admin</div>
            <h1 className="font-serif-display text-2xl text-white">Enquiries</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="text-white/80 hover:text-[#C9A24B] flex items-center gap-2 text-sm"
              data-testid="admin-refresh"
            >
              <RefreshCcw size={16} /> Refresh
            </button>
            <button
              onClick={logout}
              className="text-white/80 hover:text-[#C9A24B] flex items-center gap-2 text-sm"
              data-testid="admin-logout"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex items-center gap-2 text-white/70">
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-white/10 p-10 text-white/60">No enquiries yet.</div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => (
              <div
                key={r.id}
                className={`border p-6 ${
                  r.status === "read"
                    ? "border-white/10 bg-[#141418]"
                    : "border-[#C9A24B]/60 bg-[#17171B]"
                }`}
                data-testid={`enquiry-row-${r.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="font-serif-display text-xl text-white">{r.name}</div>
                    <div className="text-sm text-white/70">
                      {r.service} · {r.suburb || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-white/50">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                    <button
                      onClick={() => toggleStatus(r)}
                      className="text-xs uppercase tracking-widest text-[#C9A24B] hover:text-[#E6C16B] flex items-center gap-2"
                    >
                      {r.status === "read" ? <MailOpen size={14} /> : <Mail size={14} />}
                      {r.status === "read" ? "Mark unread" : "Mark read"}
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="eyebrow">Email</div>
                    <a className="text-white hover:text-[#C9A24B]" href={`mailto:${r.email}`}>
                      {r.email}
                    </a>
                  </div>
                  <div>
                    <div className="eyebrow">Phone</div>
                    <a className="text-white hover:text-[#C9A24B]" href={`tel:${r.phone}`}>
                      {r.phone}
                    </a>
                  </div>
                  <div>
                    <div className="eyebrow">Status</div>
                    <span className="text-white">{r.status}</span>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/10">
                  <div className="eyebrow mb-2">Message</div>
                  <p className="text-white/85 whitespace-pre-wrap leading-relaxed">{r.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
